// =============================================================================
// Origin UI — src/pages/admin/OnboardingTaskTemplateAdminTab.tsx
// Admin Config tab for managing onboarding task templates.
// Uses toolBar, formCard, formGrid, fieldInput patterns from admin.module.css.
// WS-6 Batch 6.3 — Onboarding Lifecycle module.
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import styles from './admin.module.css';

// ─── Types ───

interface TaskTemplate {
  taskTemplateId: string;
  officeLocationId: number | null;
  officeName: string | null;
  employmentType: string | null;
  taskName: string;
  description: string | null;
  assignedToRole: string;
  dueDayOffset: number;
  sortOrder: number;
  isActive: boolean;
}

interface OfficeLocation { officeLocationId: number; name: string; }

const ROLES = ['HRAdmin', 'Finance', 'Manager', 'NewHire'];
const EMPLOYMENT_TYPES = ['Permanent', 'FixedTerm', 'Contractor'];

const ROLE_LABELS: Record<string, string> = {
  HRAdmin: 'HR Admin',
  Finance: 'Finance',
  Manager: 'Line Manager',
  NewHire: 'New Hire',
};

function dueDayLabel(offset: number): string {
  if (offset === 0) return 'Start date';
  if (offset < 0) return `${Math.abs(offset)}d before start`;
  return `${offset}d after start`;
}

// ─── Component ───

export function OnboardingTaskTemplateAdminTab() {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offices, setOffices] = useState<OfficeLocation[]>([]);
  const [officeFilter, setOfficeFilter] = useState<string>('');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TaskTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const emptyForm = {
    officeLocationId: '' as string | number,
    employmentType: '',
    taskName: '',
    description: '',
    assignedToRole: 'HRAdmin',
    dueDayOffset: 0,
    sortOrder: 0,
    isActive: true,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true); setError('');
      const [tpls, ofcs] = await Promise.all([
        api.get<TaskTemplate[]>('/api/admin/task-templates'),
        api.get<OfficeLocation[]>('/api/reference/office-locations'),
      ]);
      setTemplates(tpls); setOffices(ofcs);
    } catch (err: any) { setError(err.message || 'Failed to load'); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm(emptyForm);
    setEditing(null);
    setCreating(true);
  }

  function startEdit(t: TaskTemplate) {
    setForm({
      officeLocationId: t.officeLocationId ?? '',
      employmentType: t.employmentType ?? '',
      taskName: t.taskName,
      description: t.description ?? '',
      assignedToRole: t.assignedToRole,
      dueDayOffset: t.dueDayOffset,
      sortOrder: t.sortOrder,
      isActive: t.isActive,
    });
    setEditing(t);
    setCreating(false);
  }

  async function handleSave() {
    if (!form.taskName.trim()) { setError('Task name is required'); return; }
    try {
      setSaving(true); setError('');
      const payload = {
        officeLocationId: form.officeLocationId ? Number(form.officeLocationId) : null,
        employmentType: form.employmentType || null,
        taskName: form.taskName,
        description: form.description || null,
        assignedToRole: form.assignedToRole,
        dueDayOffset: form.dueDayOffset,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      };
      if (editing) {
        await api.put(`/api/admin/task-templates/${editing.taskTemplateId}`, payload);
      } else {
        await api.post('/api/admin/task-templates', payload);
      }
      setCreating(false); setEditing(null); await load();
    } catch (err: any) { setError(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/admin/task-templates/${deleteTarget.id}`);
      setDeleteTarget(null); await load();
    } catch (err: any) { setError(err.message || 'Failed to delete'); setDeleteTarget(null); }
  }

  const filtered = officeFilter
    ? templates.filter(t => t.officeLocationId === Number(officeFilter) || t.officeLocationId === null)
    : templates;

  if (loading) return <LoadingSpinner message="Loading task templates..." />;

  const isEditing = creating || editing !== null;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className={styles.toolBar}>
        <span className={styles.count}>
          {filtered.length} task template{filtered.length !== 1 ? 's' : ''}
          <select
            className={styles.fieldInput}
            value={officeFilter}
            onChange={e => setOfficeFilter(e.target.value)}
            style={{ marginLeft: '12px', minWidth: '140px', display: 'inline-block', width: 'auto' }}
          >
            <option value="">All offices</option>
            {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
          </select>
        </span>
        {!isEditing && (
          <button className={styles.addBtn} onClick={startCreate}>+ Add Task Template</button>
        )}
      </div>

      {/* Create / Edit form */}
      {isEditing && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Task Template' : 'New Task Template'}</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Task Name *</label>
              <input className={styles.fieldInput} value={form.taskName} onChange={e => setForm(p => ({ ...p, taskName: e.target.value }))} placeholder="e.g. Set up DEWS registration" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Assigned To *</label>
              <select className={styles.fieldInput} value={form.assignedToRole} onChange={e => setForm(p => ({ ...p, assignedToRole: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Office</label>
              <select className={styles.fieldInput} value={form.officeLocationId} onChange={e => setForm(p => ({ ...p, officeLocationId: e.target.value }))}>
                <option value="">All offices</option>
                {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Employment Type</label>
              <select className={styles.fieldInput} value={form.employmentType} onChange={e => setForm(p => ({ ...p, employmentType: e.target.value }))}>
                <option value="">All types</option>
                {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Due Day Offset</label>
              <input className={styles.fieldInput} type="number" value={form.dueDayOffset} onChange={e => setForm(p => ({ ...p, dueDayOffset: parseInt(e.target.value) || 0 }))} />
              <span style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>Negative = before start, positive = after</span>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Sort Order</label>
              <input className={styles.fieldInput} type="number" value={form.sortOrder} onChange={e => setForm(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.fieldLabel}>Description</label>
              <input className={styles.fieldInput} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional description or instructions" />
            </div>
            {editing && (
              <div className={styles.checkField}>
                <label><input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} /> Active</label>
              </div>
            )}
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setCreating(false); setEditing(null); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</button>
          </div>
        </Card>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <Card><p style={{ color: '#9CA3AF', textAlign: 'center', padding: '30px 0' }}>No task templates configured yet.</p></Card>
      ) : (
        <Card padding={false}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Task Name</th>
                <th>Assigned To</th>
                <th>Office</th>
                <th>Type</th>
                <th>Due</th>
                <th>Active</th>
                <th style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.taskTemplateId}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{t.taskName}</div>
                    {t.description && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{t.description}</div>}
                  </td>
                  <td><span className={styles.badge}>{ROLE_LABELS[t.assignedToRole] || t.assignedToRole}</span></td>
                  <td>{t.officeName || 'All'}</td>
                  <td>{t.employmentType || 'All'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{dueDayLabel(t.dueDayOffset)}</td>
                  <td>{t.isActive ? <span style={{ color: '#059669' }}>Yes</span> : <span style={{ color: '#DC2626' }}>No</span>}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className={styles.editBtn} onClick={() => startEdit(t)}>Edit</button>
                      <button className={styles.deleteBtn} onClick={() => setDeleteTarget({ id: t.taskTemplateId, name: t.taskName })}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmDialog open={deleteTarget !== null} title="Delete Task Template"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}
